
import React, { useState, useEffect, useRef } from "react";
import IntlMessages from "@iso/components/utility/intlMessages";
import { Form, Input, Button, Select, Row, Col, DatePicker, Card, Timeline, Upload, message, Tabs, Spin, Empty, Collapse, Tag } from 'antd';
import _ from "lodash";
import AssetSummarymodal from './AssetViewModal.styles'
import moment from "moment";
import { assetService, userService, serviceLogService, assetFormService, sensorService } from "@iso/services";
import { assetStates } from "@iso/constants/assetStates";
import QRCode from "react-qr-code";
import AssetTimeline from "./AssetTimeline";
import AssetMaintenance from "./AssetMaintenance";
import AssetOverview from "./AssetOverview";


const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// camelCase / snake_case spray_data key → "Nozzle Size" style label.
const humanizeKey = (k) =>
  String(k)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const AssetViewSummary = (props) => {

    const { editHazard } = props;

  const [timeline, setTimeline] = useState([]);
  const [assetStats, setAssetStats] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [exportRange, setExportRange] = useState(null);
  const [exportRecipients, setExportRecipients] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [propertyUsers, setPropertyUsers] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loadingServiceHistory, setLoadingServiceHistory] = useState(false);
  const [activeTab, setActiveTab] = useState(props.defaultTab || "overview");
  const [liveState, setLiveState] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [forms, setForms] = useState([]);
  const [sensors, setSensors] = useState([]);

  const rangeParams = () => {
    if (exportRange && exportRange[0] && exportRange[1]) {
      return { from: exportRange[0].format("YYYY-MM-DD"), to: exportRange[1].format("YYYY-MM-DD") };
    }
    return {};
  };

  const handleExportPdf = () => {
    const assetId = props.viewSummary && props.viewSummary.id;
    if (!assetId) return;
    setExporting(true);
    assetService
      .exportServiceHistory(assetId, rangeParams())
      .then((blob) => {
        // On failure the backend returns a JSON error envelope (Content-Type
        // application/json) with HTTP 200 — don't save that as a .pdf.
        if (!blob || (blob.type && blob.type.indexOf("application/pdf") === -1)) {
          message.error("Failed to export PDF");
          return;
        }
        const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `service-history-${assetId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error("Failed to export PDF"))
      .finally(() => setExporting(false));
  };

  const handleEmailPdf = () => {
    const assetId = props.viewSummary && props.viewSummary.id;
    if (!assetId) return;
    if (!exportRecipients.length) {
      message.warning("Add at least one recipient email");
      return;
    }
    setEmailing(true);
    assetService
      .emailServiceHistory(assetId, { ...rangeParams(), recipient_emails: exportRecipients })
      .then((res) => {
        if (res && res.code === "0000") {
          // Use the server message so a partial success ("Sent to N; could not
          // send to: …") is shown rather than a blanket success.
          const hadFailures = res.data && res.data.failed && res.data.failed.length;
          (hadFailures ? message.warning : message.success)(
            (res && res.message) || "Service history emailed"
          );
          setExportRecipients([]);
        } else {
          message.error((res && res.message) || "Failed to email report");
        }
      })
      .catch(() => message.error("Failed to email report"))
      .finally(() => setEmailing(false));
  };

  // Load the merged timeline when the summary opens for an asset.
  useEffect(() => {
    const assetId = props.viewSummary && props.viewSummary.id;
    if (!props.visibleViewSummary || !assetId) {
      return;
    }
    setLoadingTimeline(true);
    assetService
      .getTimeline(assetId)
      .then((res) => {
        setTimeline((res && res.timeline) || []);
        setAssetStats((res && res.asset) || null);
        // Track the asset's live readiness state so the status banner reflects
        // defect/return-to-service changes made from the Maintenance tab.
        if (res && res.asset && res.asset.state) {
          setLiveState(res.asset.state);
        }
      })
      .finally(() => setLoadingTimeline(false));
  }, [props.visibleViewSummary, props.viewSummary && props.viewSummary.id]);

  // Load the property's contacts (users) to pre-populate the report recipients.
  const summaryPropertyId = (props.viewSummary && props.viewSummary.property_id) || null;
  useEffect(() => {
    if (!props.visibleViewSummary || !summaryPropertyId) {
      return;
    }
    userService
      .getUserForProperty(summaryPropertyId)
      .then((res) => setPropertyUsers((res && res.users) || []))
      .catch((err) => console.error("Failed to load property contacts:", err));
  }, [props.visibleViewSummary, summaryPropertyId]);

  // Open on the tab the caller asked for (e.g. "View History" -> Service History),
  // otherwise land on the Overview.
  useEffect(() => {
    if (props.visibleViewSummary) {
      setActiveTab(props.defaultTab || "overview");
    }
  }, [props.visibleViewSummary, props.defaultTab]);

  // Load the aggregated Overview payload when the summary opens for an asset.
  const loadOverview = () => {
    const id = props.viewSummary && props.viewSummary.id;
    if (!id) return;
    setLoadingOverview(true);
    assetService
      .getOverview(id)
      .then((res) => {
        setOverview((res && res.overview) || null);
        if (res && res.overview && res.overview.state) {
          setLiveState(res.overview.state);
        }
      })
      .finally(() => setLoadingOverview(false));
  };
  useEffect(() => {
    if (props.visibleViewSummary && props.viewSummary && props.viewSummary.id) {
      loadOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visibleViewSummary, props.viewSummary && props.viewSummary.id, props.refreshToken]);

  // Associated forms + linked sensors for the Asset Summary tab's collapsible
  // sections (loaded from the existing asset-form and asset-sensor endpoints).
  useEffect(() => {
    const id = props.viewSummary && props.viewSummary.id;
    if (!props.visibleViewSummary || !id) return;
    assetFormService.getForm({ asset_id: id })
      .then((r) => setForms((r && r.assetForms) || []))
      .catch(() => setForms([]));
    sensorService.getAssetSensors(id)
      .then((r) => setSensors((r && (r.sensors || r.data || r.asset_sensors || [])) || []))
      .catch(() => setSensors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visibleViewSummary, props.viewSummary && props.viewSummary.id]);

  // Load the asset's service history (service_logs) only when the History tab is
  // actually shown — avoids a /service-logs request on every Summary open
  // (including non-machinery assets that don't surface this tab).
  useEffect(() => {
    const assetId = props.viewSummary && props.viewSummary.id;
    if (!props.visibleViewSummary || !assetId || activeTab !== "history") {
      return;
    }
    setLoadingServiceHistory(true);
    serviceLogService
      .getList(assetId)
      .then((res) => setServiceHistory((res && res.service_logs) || []))
      .catch((err) => console.error("Failed to load service history:", err))
      .finally(() => setLoadingServiceHistory(false));
  }, [props.visibleViewSummary, props.viewSummary && props.viewSummary.id, activeTab]);

const cancelModal = () => {
    props.setVisibleViewSummary();
}

  const asset = props.viewSummary || {};

  // Asset Summary field list — rendered as a responsive two-column grid so it
  // fills the wider modal instead of a single stretched column.
  const detailRows = [
    { label: <IntlMessages id="assetSummary.hazardId" />, value: asset.id || "-" },
    ...(asset.type ? [{ label: <IntlMessages id="assetSummary.assetType" />, value: asset.type }] : []),
    { label: <IntlMessages id="assetSummary.title" />, value: asset.title || "-" },
    { label: <IntlMessages id="assetSummary.category" />, value: asset.category || "-" },
    { label: <IntlMessages id="assetSummary.categoryLabel" />, value: asset.category_label || "-" },
    { label: <IntlMessages id="assetSummary.description" />, value: asset.description || "-" },
    { label: <IntlMessages id="assetSummary.state" />, value: asset.state || "-" },
    { label: <IntlMessages id="assetSummary.remainingLabel" />, value: asset.remaining_label || "-" },
    { label: <IntlMessages id="assetSummary.updatedDate" />, value: asset.updated_at || "-" },
    { label: <IntlMessages id="assetSummary.createdBy" />, value: asset.created_by || "-" },
    ...(asset.category === "machinery"
      ? [
          { label: "Manufacturer", value: asset.manufacturer || "-" },
          { label: "Make & Model", value: [asset.make, asset.model].filter(Boolean).join(" ") || "-" },
          { label: "Serial / VIN", value: asset.serial_vin || "-" },
          { label: "Registration", value: asset.registration || "-" },
          { label: "Warranty Expiry", value: asset.warranty_expiry ? moment(asset.warranty_expiry).format("ll") : "-" },
          { label: "Service Interval", value: asset.service_interval ? `${asset.service_interval} ${asset.service_interval_unit || ""}`.trim() : "-" },
          { label: "Current Reading", value: (asset.current_reading !== null && asset.current_reading !== undefined && asset.current_reading !== "") ? `${asset.current_reading} ${asset.reading_unit || ""}`.trim() : "-" },
        ]
      : []),
    // Spray-specific attributes (nozzle / tank / boom …) live in the spray
    // relation's free-form spray_data blob; surface whatever it holds.
    ...(asset.category === "spray" && asset.spray
      ? [
          { label: "Spray Type", value: asset.spray.spray_type || asset.type || "-" },
          ...Object.entries(asset.spray.spray_data || {})
            .filter(([, v]) => v !== null && v !== undefined && v !== "" && typeof v !== "object")
            .map(([k, v]) => ({ label: humanizeKey(k), value: String(v) })),
        ]
      : []),
  ];

  // Passport deep-link the QR encodes. Points back at THIS web app's origin so a
  // scan from any phone camera opens the live passport (no dedicated app needed).
  const passportUrl = asset.qr_token
    ? `${window.location.origin}/asset-passport/${asset.qr_token}`
    : null;

  // Resolve the asset's current state to a display label + colour (shared with
  // the rest of the app via assetStates). locked_out drives the DO NOT OPERATE
  // banner so a restricted machine is impossible to miss on the passport.
  const stateMeta = (() => {
    const raw = liveState || asset.state || "";
    const match = assetStates.find((s) => s.value === raw);
    return {
      raw,
      label: match ? match.label : asset.state_label || (raw ? raw : "Unknown"),
      color: match ? match.color : "rgb(120, 120, 120)",
      lockedOut: raw === "locked_out",
      // Both a formal lock-out and a critical-defect out_of_service mean the same
      // to a worker at the machine: do not operate.
      doNotOperate: raw === "locked_out" || raw === "out_of_service",
      restricted: raw === "restricted",
    };
  })();

  // Re-pull the asset's live state + timeline after a Maintenance action so the
  // status banner and Timeline tab reflect the new readiness immediately.
  const refreshAssetState = () => {
    const id = asset.id;
    if (!id) return;
    assetService.getTimeline(id).then((res) => {
      setTimeline((res && res.timeline) || []);
      if (res && res.asset && res.asset.state) {
        setLiveState(res.asset.state);
      }
    });
  };

  const qrRef = useRef(null);

  // Open the QR in a bare print window so a worker can stick a durable label on
  // the physical asset. Reads the already-rendered SVG rather than re-generating.
  const handlePrintQr = () => {
    const holder = qrRef.current;
    const svg = holder && holder.querySelector("svg");
    if (!svg) {
      return;
    }
    const win = window.open("", "_blank", "width=480,height=640");
    if (!win) {
      message.warning("Please allow pop-ups to print the QR label");
      return;
    }
    const label = (asset.title || "Asset").replace(/</g, "&lt;");
    win.document.write(
      `<html><head><title>QR — ${label}</title>` +
        "<style>body{font-family:Arial,Helvetica,sans-serif;text-align:center;padding:24px;margin:0;}" +
        ".t{font-size:18px;font-weight:bold;margin:16px 0 4px;}.i{color:#555;font-size:12px;}" +
        ".b{margin-top:16px;color:#2A4A3A;font-weight:bold;letter-spacing:1px;}svg{width:240px;height:240px;}</style>" +
        "</head><body onload=\"window.print()\">" +
        `<div>${svg.outerHTML}</div>` +
        `<div class="t">${label}</div>` +
        `<div class="i">Asset ID: ${asset.id || "-"}</div>` +
        '<div class="b">AirAgri</div>' +
        "</body></html>"
    );
    win.document.close();
  };

  return (

    <AssetSummarymodal
    className='visitor-modal'
    forceRender={true}
    open={props.visibleViewSummary}
    title={<IntlMessages id="Asset Details" />}
    onCancel={cancelModal}
    maskClosable={true}
    footer={[
      <Button key="back" onClick={cancelModal} className="cancel-button">
        {<IntlMessages id="propertyPage.modal.cancel" />}
      </Button>
    ]}
  >
    <div style={{
        padding:'0px 30px 30px 30px',
        width: "100%", height: "auto"
      }}>
        <br />
        {/* Live status banner — the passport's headline. A do-not-operate asset
            (locked out or critical-defect out-of-service) shows an unmistakable
            red bar; a restricted asset shows an amber caution; anything else shows
            a coloured status pill so readiness is clear at a glance. */}
        {stateMeta.doNotOperate ? (
          <div
            style={{
              background: "#e24e4c",
              color: "#fff",
              padding: "14px 18px",
              borderRadius: 8,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>⛔</span>
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
              DO NOT OPERATE — {stateMeta.lockedOut ? "LOCKED OUT" : "OUT OF SERVICE"}
            </span>
          </div>
        ) : stateMeta.restricted ? (
          <div
            style={{
              background: "#f5a01b",
              color: "#fff",
              padding: "14px 18px",
              borderRadius: 8,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>⚠️</span>
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
              RESTRICTED — USE WITH CAUTION
            </span>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 16px",
              borderRadius: 8,
              marginBottom: 16,
              background: "rgba(0,0,0,0.03)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: stateMeta.color,
                flex: "0 0 auto",
              }}
            />
            <span style={{ color: "rgba(0,0,0,0.55)" }}>Status:</span>
            <b style={{ color: stateMeta.color }}>{stateMeta.label}</b>
          </div>
        )}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<IntlMessages id="Overview" />} key="overview">
          <AssetOverview
            overview={overview}
            loading={loadingOverview}
            assetId={asset.id}
            modalVisible={props.visibleViewSummary}
            onRefresh={loadOverview}
            onStartPreStart={props.onStartPreStart ? () => props.onStartPreStart(asset) : undefined}
            onAssignOperator={props.onAssignOperator ? () => props.onAssignOperator(asset) : undefined}
            onUnassignOperator={props.onUnassignOperator ? () => props.onUnassignOperator(asset) : undefined}
            onLogService={props.onLogService ? () => props.onLogService(asset) : undefined}
            onReportIssue={() => setActiveTab("maintenance")}
            onPrintQr={() => setActiveTab("qr")}
          />
        </TabPane>
        <TabPane tab={<IntlMessages id="Asset Summary" />} key="details">
          <Collapse defaultActiveKey={["details"]} expandIconPosition="right">
            <Collapse.Panel header={<b>Asset Details</b>} key="details">
              <Row gutter={[24, 0]}>
                {detailRows.map((r, i) => (
                  <Col xs={24} md={12} key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                      <span className="card_content" style={{ color: "rgba(0,0,0,0.55)" }}>{r.label}</span>
                      <b className="card_value" style={{ textAlign: "right" }}>{r.value}</b>
                    </div>
                  </Col>
                ))}
              </Row>
            </Collapse.Panel>
            <Collapse.Panel header={<b>Forms{forms.length ? ` (${forms.length})` : ""}</b>} key="forms">
              {forms.length === 0 ? (
                <Empty description="No forms linked" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                forms.map((f, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                    <span>{(f.form && (f.form.name || f.form.survey_title)) || "Form"}</span>
                    {f.form && f.form.type ? <Tag>{f.form.type}</Tag> : null}
                  </div>
                ))
              )}
            </Collapse.Panel>
            <Collapse.Panel header={<b>Sensors{sensors.length ? ` (${sensors.length})` : ""}</b>} key="sensors">
              {!sensors || sensors.length === 0 ? (
                <Empty description="No sensors linked" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                sensors.map((s, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                    <span>{s.name || s.device_name || s.sensor_name || s.title || `Sensor ${i + 1}`}</span>
                    <b>{s.last_reading || s.latest_value || s.value || (s.battery ? `${s.battery}%` : "")}</b>
                  </div>
                ))
              )}
            </Collapse.Panel>
          </Collapse>
        </TabPane>
        <TabPane tab={<IntlMessages id="Service History" />} key="history">
          {loadingServiceHistory ? (
            <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
          ) : serviceHistory.length === 0 ? (
            <Empty description={<IntlMessages id="No service history yet" />} />
          ) : (
            <Timeline style={{ marginTop: 12 }}>
              {serviceHistory.map((log) => {
                const typeLabel = log.service_type
                  ? log.service_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                  : "Service";
                return (
                  <Timeline.Item color="green" key={log.id}>
                    <Card
                      size="small"
                      title={`${moment(log.service_date).format("ll")} — ${typeLabel}`}
                      style={{ marginBottom: 8, borderRadius: 8 }}
                    >
                      <Row gutter={[8, 4]}>
                        {log.reading !== null && log.reading !== undefined && log.reading !== "" && (
                          <Col span={12}><b>Reading:</b> {`${log.reading} ${log.reading_unit || ""}`.trim()}</Col>
                        )}
                        {log.total_cost !== null && log.total_cost !== undefined && (
                          <Col span={12}><b>Total:</b> ${Number(log.total_cost).toFixed(2)}</Col>
                        )}
                      </Row>
                      {(log.items || []).length > 0 && (
                        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                          {log.items.map((it) => (
                            <li key={it.id}>
                              <b style={{ textTransform: "capitalize" }}>{it.action}</b>
                              {it.description ? ` — ${it.description}` : ""}
                              {(it.parts || []).length > 0 && (
                                <span style={{ color: "rgba(0,0,0,0.55)" }}>
                                  {" "}({it.parts.map((p) => p.part_name).filter(Boolean).join(", ")})
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {log.notes && (
                        <p style={{ margin: "6px 0 0", color: "rgba(0,0,0,0.55)" }}>{log.notes}</p>
                      )}
                    </Card>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}
        </TabPane>
        <TabPane tab={<IntlMessages id="History & Activity" />} key="timeline">
          {loadingTimeline ? (
            <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
          ) : (
            <>
              <Card bordered={true} style={{ borderRadius: '8px', marginBottom: 16 }}>
                <Row gutter={[8, 8]} align="middle">
                  <Col xs={24} md={8}>
                    <RangePicker
                      style={{ width: '100%' }}
                      value={exportRange}
                      onChange={setExportRange}
                      format="DD/MM/YYYY"
                    />
                  </Col>
                  <Col xs={24} md={6}>
                    <Button type="primary" loading={exporting} onClick={handleExportPdf} style={{ width: '100%' }}>
                      Export PDF
                    </Button>
                  </Col>
                  <Col xs={24} md={7}>
                    <Select
                      mode="tags"
                      value={exportRecipients}
                      onChange={setExportRecipients}
                      style={{ width: '100%' }}
                      placeholder="Select contacts or type emails"
                      tokenSeparators={[",", " "]}
                      optionFilterProp="children"
                    >
                      {propertyUsers.filter((u) => u.email).map((u) => (
                        <Option key={u.email} value={u.email}>
                          {u.name} {u.surname} ({u.email})
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={3}>
                    <Button loading={emailing} onClick={handleEmailPdf} style={{ width: '100%' }}>
                      Email
                    </Button>
                  </Col>
                </Row>
                <p style={{ color: 'rgba(0,0,0,0.45)', margin: '8px 0 0' }}>
                  Leave the date range empty to export the full service history.
                </p>
              </Card>
              {assetStats && (
                <Card bordered={true} style={{ borderRadius: '8px', marginBottom: 16 }}>
                  <Row gutter={16} align="middle">
                    {assetStats.photo && (
                      <Col xs={24} md={6}>
                        <img
                          src={assetStats.photo}
                          alt={assetStats.title || 'Asset'}
                          style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
                        />
                      </Col>
                    )}
                    <Col xs={24} md={assetStats.photo ? 18 : 24}>
                      <h3 style={{ marginBottom: 8 }}>{assetStats.title}</h3>
                      <Row>
                        <Col span={12}><b>Reading:</b> {(assetStats.current_reading !== null && assetStats.current_reading !== undefined) ? `${assetStats.current_reading} ${assetStats.reading_unit || ''}`.trim() : '-'}</Col>
                        <Col span={12}><b>State:</b> {assetStats.state || '-'}</Col>
                        <Col span={12}><b>Warranty:</b> {assetStats.warranty_expiry ? moment(assetStats.warranty_expiry).format('ll') : '-'}</Col>
                        <Col span={12}><b>Next service:</b> {assetStats.next_service_due_date ? moment(assetStats.next_service_due_date).format('ll') : '-'}</Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              )}
              <AssetTimeline events={timeline} />
            </>
          )}
        </TabPane>
        {asset.category === "machinery" && (
        <TabPane tab={<IntlMessages id="Maintenance" />} key="maintenance">
          <AssetMaintenance
            asset={asset}
            visible={activeTab === "maintenance"}
            onStateChange={refreshAssetState}
          />
        </TabPane>
        )}
        <TabPane tab={<IntlMessages id="QR Code" />} key="qr">
          {passportUrl ? (
            <div style={{ textAlign: "center", padding: "24px 8px" }}>
              <div
                ref={qrRef}
                style={{
                  display: "inline-block",
                  background: "#fff",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <QRCode value={passportUrl} size={200} />
              </div>
              <p style={{ marginTop: 16, color: "rgba(0,0,0,0.55)" }}>
                Scan with a phone camera to open this asset's live passport.
              </p>
              <p style={{ wordBreak: "break-all", color: "rgba(0,0,0,0.35)", fontSize: 12 }}>
                {passportUrl}
              </p>
              <Button type="primary" onClick={handlePrintQr} style={{ marginTop: 8 }}>
                Print QR Label
              </Button>
            </div>
          ) : (
            <Empty description={<IntlMessages id="QR code will be available after the asset is saved." />} />
          )}
        </TabPane>
        </Tabs>
      </div>
      </AssetSummarymodal>
  );
};

export default AssetViewSummary;
